/**
 * Como a fila entende uma reunião: o ritual, a data e o mês em que ela cai.
 *
 * <p>Lógica pura, num arquivo só, porque é tudo régua — e régua sem teste diverge.
 * A fila em si é uma linha do tempo ordenada pelo dia da reunião; o que está aqui é
 * o que transforma um registro cru no que a tela precisa mostrar.
 */

/**
 * Uma reunião que se repete no calendário do time.
 *
 * <p>Cor e dia não são decoração: a Weekly cai toda segunda e a Ordinária toda
 * quarta, então a cor identifica o ritual de relance numa lista longa, e o dia
 * esperado permite <b>desconfiar de uma data errada</b> — ver {@link diaForaDoRitual}.
 */
export interface Ritual {
  chave: string;
  rotulo: string;
  cor: string;
  /** 0 = domingo … 6 = sábado, como no `getDay()`. */
  diaEsperado: number;
  /** Como o dia esperado se escreve, para a tela poder explicar o aviso. */
  nomeDoDia: string;
}

/**
 * Os rituais conhecidos, achados pelo título.
 *
 * <p>As cores fogem de propósito das que os selos de formato usam (azul no TXT,
 * vermelho no PDF, ouro no HTML): duas escalas de cor no mesmo item precisam
 * significar coisas diferentes sem se confundirem.
 */
const RITUAIS: (Ritual & { casa: RegExp })[] = [
  {
    chave: 'weekly',
    rotulo: 'Weekly Updates',
    cor: '#4e9c6b',
    diaEsperado: 1,
    nomeDoDia: 'segunda',
    casa: /weekly/i,
  },
  {
    chave: 'ordinaria',
    rotulo: 'Reunião Ordinária',
    cor: '#b06ab3',
    diaEsperado: 3,
    nomeDoDia: 'quarta',
    casa: /ordin[áa]ria/i,
  },
];

/** O ritual de uma reunião, ou nulo quando ela é avulsa. */
export function ritualDe(titulo: string): Ritual | null {
  const achado = RITUAIS.find((r) => r.casa.test(titulo));
  if (!achado) return null;
  const { casa, ...ritual } = achado;
  return ritual;
}

/**
 * Uma data ISO em partes, <b>sem passar por `new Date(iso)`</b>.
 *
 * <p>`new Date('2026-08-03')` é lido como UTC e, em qualquer fuso negativo — o Brasil
 * inteiro —, volta como 2 de agosto. Numa tela cujo assunto é a data, errar o dia por
 * um é o defeito mais caro possível.
 */
export function partesDoDia(iso: string): { dia: string; semana: string; ehValida: boolean } {
  const [ano, mes, dia] = iso.split('-').map(Number);
  if (!ano || !mes || !dia) return { dia: '—', semana: '', ehValida: false };
  const data = new Date(ano, mes - 1, dia);
  return {
    dia: String(dia).padStart(2, '0'),
    semana: data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
    ehValida: true,
  };
}

/**
 * Quando a reunião caiu num dia que o ritual não usa.
 *
 * <p>Existe porque o erro é comum e silencioso: sem informar a data, o envio assume
 * hoje — e a transcrição da segunda, subida na quinta, entra como quinta. A fila
 * inteira é ordenada por esse campo, então a história do projeto sai fora de ordem
 * sem nada reclamar. Uma Weekly numa quinta é quase sempre isso.
 */
export function diaForaDoRitual(iso: string, ritual: Ritual | null): boolean {
  if (!ritual) return false;
  const [ano, mes, dia] = iso.split('-').map(Number);
  if (!ano || !mes || !dia) return false;
  return new Date(ano, mes - 1, dia).getDay() !== ritual.diaEsperado;
}

/**
 * A data escondida no nome do arquivo.
 *
 * <p>O time nomeia os diários como {@code 08-03 - GG Weekly Updates}, e essa data é
 * mais confiável que o padrão do formulário (hoje): quem sobe na quinta a reunião da
 * segunda vai aceitar o padrão sem perceber. Aceita {@code aaaa-mm-dd} e {@code mm-dd}.
 *
 * <p>Com só mês e dia, o ano sai de uma regra simples: <b>reunião é sempre passado</b>.
 * Se montar com o ano corrente cair no futuro, era do ano anterior — é o que faz um
 * "12-28" aberto em janeiro apontar para dezembro passado, e não para daqui a onze meses.
 */
export function dataDoNome(nome: string, hoje: Date): string | null {
  const completo = nome.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (completo) {
    const [, ano, mes, dia] = completo;
    return valida(Number(ano), Number(mes), Number(dia)) ? `${ano}-${mes}-${dia}` : null;
  }

  // Sem ano: exige começo do nome, senão "v1-20 - notas" viraria 20 de janeiro.
  const curto = nome.match(/^(\d{2})-(\d{2})(?!\d)/);
  if (!curto) return null;

  const mes = Number(curto[1]);
  const dia = Number(curto[2]);
  if (!valida(hoje.getFullYear(), mes, dia)) return null;

  const doAnoCorrente = new Date(hoje.getFullYear(), mes - 1, dia);
  const ano = doAnoCorrente > hoje ? hoje.getFullYear() - 1 : hoje.getFullYear();
  return `${ano}-${curto[1]}-${curto[2]}`;
}

/**
 * O nome do arquivo sem a data e sem a extensão.
 *
 * <p>A data sai porque ela virou campo próprio, e é o quadrado da fila que a mostra:
 * repetida no título, ela aparece duas vezes na mesma linha — e a do título é a que
 * ninguém consegue corrigir depois. Guardar a mesma informação em dois lugares é o
 * caminho conhecido para os dois discordarem.
 */
export function tituloSemData(nome: string): string {
  return nome
    .replace(/\.[^.]+$/, '')
    .replace(/^\d{4}-\d{2}-\d{2}\s*[-–—]?\s*/, '')
    .replace(/^\d{2}-\d{2}(?!\d)\s*[-–—]?\s*/, '')
    .trim();
}

/** Se a data existe de verdade — 02-31 casa com o padrão e não é dia nenhum. */
function valida(ano: number, mes: number, dia: number): boolean {
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return false;
  const data = new Date(ano, mes - 1, dia);
  return data.getMonth() === mes - 1 && data.getDate() === dia;
}

export interface MesDaFila<T> {
  /** aaaa-mm, para a chave do React e para ordenar. */
  chave: string;
  /** "Agosto de 2026" — com a inicial maiúscula, que o `toLocaleDateString` não dá. */
  rotulo: string;
  reunioes: T[];
}

/**
 * A fila quebrada por mês, preservando a ordem que veio do servidor.
 *
 * <p>São muitas reuniões por ano — duas por semana, uma centena por temporada. Sem
 * quebra, a linha do tempo vira uma lista sem marco: rolar quarenta itens para achar
 * "aquela de junho" é o que a divisão por mês evita.
 */
export function agrupaPorMes<T extends { reuniaoEm: string }>(lista: T[]): MesDaFila<T>[] {
  const meses: MesDaFila<T>[] = [];
  for (const item of lista) {
    const chave = item.reuniaoEm.slice(0, 7);
    const ultimo = meses[meses.length - 1];
    if (ultimo?.chave === chave) {
      ultimo.reunioes.push(item);
      continue;
    }
    meses.push({ chave, rotulo: rotuloDoMes(chave), reunioes: [item] });
  }
  return meses;
}

function rotuloDoMes(chave: string): string {
  const [ano, mes] = chave.split('-').map(Number);
  if (!ano || !mes) return chave;
  const nome = new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}
